import React, {
  ReactNode,
  useState,
  useCallback,
  createRef,
  useRef,
  useEffect,
} from 'react';
import styled, { css, keyframes } from 'styled-components';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import lodash from 'lodash';
import ReactGA from 'react-ga';
import ImageUploader from 'react-images-upload';

import { media } from './Layout';

import { searchKeywordState } from '../../modules/searchKeyword';

import IconSearch from '../../assets/icon/ic-search@2x.png';
import IconCamera from '../../assets/icon/ic-camera@2x.png';

import config from '../../lib/config-api.json';
import { GetSimilarImageParamType } from '../../lib/tabSimilarImage/type';

const env = process.env.NODE_ENV;
let { apiInfo } = config;

if (env === 'development') {
  apiInfo = config.apiInfo_dev;
}

interface styleType {
  isFocus: boolean;
}

const InputBox = styled.div`
  /* All Device */
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;

  /* Mobile Device */
  max-width: 1000px;

  ${media.desktop`
    max-width: 690px;
  `};
`;

const NavInput = styled.input`
  font-family: 'NotoKrM';
  padding-left: 24px;
  padding-right: 40px;
  width: 100%;
  border: solid 1px #cccccc;
  z-index: 2;
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.03);
  :hover {
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16);
  }
  :focus {
    outline: none;
  }

  /* Mobile Device */
  height: 46px;
  border-radius: 23px;
  font-size: 15px;

  ${media.desktop` 
    height: 50px;
    border-radius: 30px;
    font-size: 18px;
  `};
  ${(props: styleType) => {
    if (props.isFocus) {
      return css`
        box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16);
        ${media.desktop` 
          border-bottom-left-radius: 0px;
          border-bottom-right-radius: 0px;
        `};
      `;
    }
    return css``;
  }}
`;

const ExtendedInputBox = styled.div`
  position: absolute;
  display: none;
  flex-direction: column;
  padding-top: 21px;
  padding-bottom: 11px;
  padding-left: 24px;
  padding-right: 24px;
  z-index: 3;
  top: 50px;
  width: 100%;
  font-size: 14px;
  background-color: white;
  border: solid 1px #cccccc;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16);

  /* Mobile Device */
  border-radius: 24px;

  ${media.desktop`
    border-radius: 0px;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
  `};

  ${(props: styleType) => {
    if (props.isFocus) {
      return css`
        display: flex;
      `;
    }
    return css``;
  }}
`;

const AutoCompleteItem = styled.div`
  font-family: 'NotoKrL';
  width: 100%;
  margin-bottom: 10px;
  color: #333333;
  z-index: 3;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`;

const RecentKeywordItem = styled.div`
  font-family: 'NotoKrR';
  width: 100%;
  margin-bottom: 10px;
  color: #333333;
  z-index: 3;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`;

const SearchBox = styled.div`
  display: flex;
  position: absolute;
  z-index: 2;
  right: 21px;
  height: 22px;
`;

const SearchLink = styled.div`
  cursor: pointer;
  display: flex;
  margin-left: 15px;
  .fileUploader {
    display: none;
  }
`;

const IconSearchImg = styled.img`
  /* Mobile Device */
  height: 19px;

  ${media.desktop` 
    height: 22px;
  `};
`;

interface SearchInputFormProps extends RouteComponentProps {
  urlParam: string;
  searchKeyword: searchKeywordState['searchKeyword'];
  onSetSearchKeyword: (
    searchKeyword: searchKeywordState['searchKeyword']
  ) => void;
  onGoToResult: (q: string | string[]) => void;
  onGoToAI: (q: string | string[]) => void;
  onGoToImage: (q: string | string[]) => void;
  onGoToSimilarImage: (q: string | string[]) => void;
  onGetSimilarImage: (info: GetSimilarImageParamType) => void;
}
interface SearchInputFormState {
  autoComplete: string[] | [];
  recentKeyword: { keyword: string; count: number }[] | null;
}

const SearchInputForm = ({
  searchKeyword,
  location,
  onSetSearchKeyword,
  onGoToResult,
  onGoToAI,
  onGoToImage,
  onGoToSimilarImage,
  onGetSimilarImage,
}: SearchInputFormProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocus, setIsFocus] = useState(false);
  const [autoComplete, setAutoComplete] = useState<
    SearchInputFormState['autoComplete']
  >([]);
  const [recentKeyword, setRecentKeyword] = useState<
    SearchInputFormState['recentKeyword']
  >([]);

  const searchStart = useCallback(
    lodash.debounce(() => {
      const search = document.getElementById('aivory-search');

      if (search) {
        setIsFocus(false);
        switch (window.location.pathname) {
          case '/search/result':
            onGoToResult(searchKeyword);
            break;
          case '/search/ai':
            onGoToAI(searchKeyword);
            break;
          case '/search/image':
            onGoToImage(searchKeyword);
            break;
          case '/search/similarImage':
            onGoToResult(searchKeyword);
            break;
          default:
            onGoToResult(searchKeyword);
            break;
        }
        setIsFocus(false);
      }
    }, 100),
    [searchKeyword]
  );

  const searchImageStart = () => {
    const uploaderEl = document.getElementsByClassName('aivory-image-uploader');

    if (uploaderEl) {
      const uploaderElItem = uploaderEl.item(0) as HTMLElement;
      if (uploaderElItem) {
        uploaderElItem.click();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('load', () => {
      if (inputRef.current) {
        // setKeyword(inputRef.current.value);
      }
    });
  }, []);

  const KeyUpEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13) {
      switch (location.pathname) {
        case '/':
          ReactGA.ga('send', 'event', `keyup_main_search_enter`);
          break;
        default:
          ReactGA.ga('send', 'event', `keyup_search_search_enter`);
          break;
      }

      searchStart();
    }
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSetSearchKeyword(e.target.value);
    e.preventDefault();

    const data = {
      query: e.target.value,
    };
    axios({
      method: 'get',
      url: `${apiInfo.api_url}/autocomplete/`,
      timeout: 10000,
      params: data,
    })
      .then((response) => {
        const temp = response.data.payload;

        if (temp.length > 0) {
          setIsFocus(true);
        } else {
          setIsFocus(false);
        }
        setAutoComplete(temp);
      })
      .catch((error) => {
        setAutoComplete([]);
      });
  };

  const onBlurInput = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      setIsFocus(false);
    }, 100);
  };

  const onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
    switch (location.pathname) {
      case '/':
        ReactGA.ga('send', 'event', `focus_main_search_input_form`);
        break;

      default:
        ReactGA.ga('send', 'event', `focus_search_search_input_form`);
        break;
    }

    const data = {
      count: 10,
      session: localStorage.getItem('aivory_session_id') || 'test_session',
    };
    axios({
      method: 'get',
      url: `${apiInfo.api_url}/recent_keyword/`,
      timeout: 10000,
      params: data,
    })
      .then((response) => {
        const temp = response.data.payload;

        if (temp.length > 0) {
          setRecentKeyword(temp);
          setIsFocus(true);
        } else {
          setIsFocus(false);
        }
      })
      .catch((error) => {
        setRecentKeyword([]);
        setIsFocus(false);
      });
  };

  const makeAutoCompleteList = (): ReactNode[] | null => {
    const result: ReactNode[] | null = [];

    if (searchKeyword.length === 0 || searchKeyword === null) {
      if (recentKeyword) {
        recentKeyword.forEach((element) => {
          result.push(
            <RecentKeywordItem
              onMouseDown={() => {
                ReactGA.ga('send', 'event', `click_최근검색어`);
                onSetSearchKeyword(element.keyword);
                onGoToResult(element.keyword);
              }}
            >
              {element.keyword}
            </RecentKeywordItem>
          );
        });
      }
    } else {
      autoComplete.forEach((element) => {
        result.push(
          <AutoCompleteItem
            onMouseDown={() => {
              ReactGA.ga('send', 'event', `click_자동완성`);
              onSetSearchKeyword(element);
              onGoToResult(element);
            }}
          >
            {element}
          </AutoCompleteItem>
        );
      });
    }

    return result;
  };

  const onDrop = (files: File[], pictures: string[]) => {
    onGetSimilarImage({ image: files[0] });
    onGoToSimilarImage('이미지 검색');
  };

  return (
    <InputBox>
      <NavInput
        type="text"
        placeholder="관광 관련 키워드를 검색해 주세요."
        ref={inputRef}
        onKeyUp={KeyUpEnter}
        onFocus={onFocusInput}
        onChange={onChangeInput}
        onBlur={onBlurInput}
        isFocus={isFocus}
        value={searchKeyword}
      />
      <SearchBox>
        <SearchLink
          id="aivory-image-search"
          onClick={() => {
            switch (location.pathname) {
              case '/':
                ReactGA.ga('send', 'event', `click_main_image_search_button`);
                break;
              default:
                ReactGA.ga('send', 'event', `click_search_image_search_button`);
                break;
            }
            searchImageStart();
          }}
        >
          <ImageUploader
            // id="aivory-image-uploader"
            buttonClassName="aivory-image-uploader"
            withIcon={false}
            onChange={onDrop}
            imgExtension={['.jpg', '.gif', '.png', '.gif', '.jpeg']}
            maxFileSize={52428800}
            fileSizeError="파일 크기는 50mb 를 넘을 수 없습니다."
            fileTypeError="지원하지 않는 포맷 입니다."
            withLabel={false}
            singleImage
          />
          <IconSearchImg src={IconCamera} alt="image search icon" />
        </SearchLink>
        <SearchLink
          id="aivory-search"
          onClick={() => {
            switch (location.pathname) {
              case '/':
                ReactGA.ga('send', 'event', `click_main_search_button`);
                break;
              default:
                ReactGA.ga('send', 'event', `click_search_search_button`);
                break;
            }
            searchStart();
          }}
        >
          <IconSearchImg src={IconSearch} alt="search icon" />
        </SearchLink>
      </SearchBox>
      <ExtendedInputBox isFocus={isFocus}>
        {makeAutoCompleteList()}
      </ExtendedInputBox>
    </InputBox>
  );
};

export default withRouter(SearchInputForm);
